/**
 * usePatientImportUpload Hook
 *
 * Handles CSV file upload for patient import.
 * Wraps the upload mutation and provides file validation and org ID.
 */

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganizationId } from '@features/user';
import SentryService from '@services/SentryService';
import { useEventTracking } from '@services/statsig';

import { useUploadImportFileMutation } from '../store/hooks';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

export interface UsePatientImportUploadReturn {
  organizationId: string | undefined;
  isReady: boolean;
  selectedFile: File | null;
  isDragging: boolean;
  isUploading: boolean;
  error: string | null;
  handleFileSelect: (file: File) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleUpload: () => Promise<void>;
  clearFile: () => void;
  clearError: () => void;
}

export function usePatientImportUpload(): UsePatientImportUploadReturn {
  const navigate = useNavigate();
  const organizationId = useOrganizationId();
  const { patientImport } = useEventTracking();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [uploadFile, { isLoading: isUploading }] = useUploadImportFileMutation();

  const validateFile = useCallback((file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds 100MB limit. Selected file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`;
    }

    return null;
  }, []);

  const handleFileSelect = useCallback(
    (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        setSelectedFile(null);
        return;
      }
      setError(null);
      setSelectedFile(file);
      patientImport.fileSelected({
        file_type: file.name.split('.').pop() ?? 'unknown',
        file_size_kb: Math.round(file.size / 1024),
      });
    },
    [validateFile, patientImport],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleUpload = useCallback(async () => {
    if (!selectedFile || !organizationId) return;

    const fileType = selectedFile.name.split('.').pop() ?? 'unknown';
    const fileSizeKb = Math.round(selectedFile.size / 1024);
    patientImport.uploadStarted({ file_type: fileType, file_size_kb: fileSizeKb });

    try {
      const result = await uploadFile({
        file: selectedFile,
        organizationId,
      }).unwrap();

      if (result.session_id) {
        patientImport.uploadCompleted({ session_id: result.session_id });
        navigate(`/patients/import/${result.session_id}`);
      }
    } catch (err: unknown) {
      let errorMessage = 'Failed to upload file. Please try again.';
      const apiErr = err as { data?: { error?: string; message?: string; detail?: string }; status?: number };
      if (apiErr?.data?.error) {
        errorMessage = apiErr.data.error;
      } else if (apiErr?.data?.message) {
        errorMessage = apiErr.data.message;
      } else if (apiErr?.data?.detail) {
        errorMessage = apiErr.data.detail;
      } else if (apiErr?.status === 413) {
        errorMessage = 'File is too large. Maximum file size is 100MB.';
      } else if (apiErr?.status === 422) {
        errorMessage = 'Invalid file format. Please try a different file.';
      }
      patientImport.uploadFailed({ error_message: errorMessage, status_code: apiErr?.status });
      SentryService.captureException(err, {
        level: 'error',
        tags: { component: 'usePatientImportUpload', feature: 'patient-import' },
        extra: { status: apiErr?.status, errorMessage },
      });
      setError(errorMessage);
    }
  }, [selectedFile, organizationId, uploadFile, navigate, patientImport]);

  const clearFile = useCallback(() => {
    setSelectedFile(null);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    organizationId: organizationId ?? undefined,
    isReady: !!organizationId,
    selectedFile,
    isDragging,
    isUploading,
    error,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleFileInputChange,
    handleUpload,
    clearFile,
    clearError,
  };
}
